/*
 * home-slider.js — lightweight, dependency-free horizontal slider for the
 * homepage Tailwind blocks. Replaces Swiper. Layout/peek/slide widths are
 * pure CSS (flex + scroll-snap via Tailwind utilities); this script adds:
 *   • mouse drag-to-scroll (touch/trackpad already work via native overflow)
 *   • optional INFINITE ("eternity") looping — opt in with `data-loop`
 *   • optional autoplay that advances one slide and loops, pausing on interaction
 *   • click suppression while dragging
 *
 * INFINITE LOOP (data-loop): we clone the full slide set once BEFORE and once
 * AFTER the originals, giving three identical bands [cloneA][originals][cloneB].
 * We start centered on the middle band and, whenever scrollLeft drifts a full
 * band-width away from the origin, we instantly add/subtract one band width.
 * Because the band the user lands on is pixel-identical to the one they left,
 * the jump is invisible — the slider scrolls forever in either direction.
 * Band width is measured from real node offsets (clone.offsetLeft -
 * original.offsetLeft) so it stays correct even when the track has horizontal
 * page-width padding.
 *
 * Markup contract (set by the block):
 *   <div data-home-slider [data-loop] [data-autoplay] [data-autoplay-speed="5000"]>
 *     <div data-home-slider-track class="flex overflow-x-auto snap-x ...">
 *       <div data-home-slide>…</div> ×N
 *     </div>
 *   </div>
 */
(function () {
  function initSlider(root) {
    if (root.dataset.homeSliderReady === 'true') return;
    root.dataset.homeSliderReady = 'true';

    var track = root.querySelector('[data-home-slider-track]') || root;
    if (!track) return;

    var wantLoop = root.hasAttribute('data-loop');
    var looping = false;
    var setWidth = 0;   // width of one band (the original slide set)
    var origin = 0;     // scrollLeft that centers the middle (real) band
    var lock = false;   // suppress wrap while we reposition programmatically

    function gap() {
      var s = getComputedStyle(track);
      return parseFloat(s.columnGap || s.gap || '0') || 0;
    }

    // Instantly move the scroll position WITHOUT triggering a wrap feedback loop.
    // The scrollLeft assignment fires its own async `scroll` event, which must be
    // ignored or wrap() re-enters. We hold `lock` until the next frame so that
    // event is skipped. (Looping sliders run with scroll-snap DISABLED — see
    // buildLoop — because snap-mandatory would re-snap after every jump and fight
    // the wrap into an infinite loop that freezes the page.)
    function reposition(x) {
      lock = true;
      track.scrollLeft = x;
      requestAnimationFrame(function () { lock = false; });
    }

    // ---- build the infinite loop (clone before + after) ----
    function buildLoop() {
      if (looping) return true;
      // nothing to loop if the content already fits
      if (track.scrollWidth <= track.clientWidth + 4) return false;
      var originals = Array.prototype.slice.call(track.children);
      if (originals.length < 2) return false;

      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();
      var firstAfterClone = null;

      originals.forEach(function (node, i) {
        var a = node.cloneNode(true);
        var b = node.cloneNode(true);
        a.setAttribute('data-clone', ''); a.setAttribute('aria-hidden', 'true'); a.tabIndex = -1;
        b.setAttribute('data-clone', ''); b.setAttribute('aria-hidden', 'true'); b.tabIndex = -1;
        before.appendChild(a);
        if (i === 0) firstAfterClone = b;
        after.appendChild(b);
      });

      track.appendChild(after);
      track.insertBefore(before, track.firstChild);

      // band width = distance between a real slide and its appended clone
      setWidth = firstAfterClone.offsetLeft - originals[0].offsetLeft;
      if (!(setWidth > 0)) {            // layout not ready — bail, caller retries
        // undo clones so a later retry starts clean
        Array.prototype.slice.call(track.querySelectorAll('[data-clone]')).forEach(function (n) { n.remove(); });
        return false;
      }

      // Snap-mandatory + clone-jumping oscillate (the browser re-snaps right
      // back past the wrap boundary). Free-scroll is the norm for infinite
      // carousels, so disable snap once we're looping.
      track.style.scrollSnapType = 'none';

      var padL = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      track.scrollLeft = originals[0].offsetLeft - padL;  // show first real slide at the gutter
      origin = track.scrollLeft;
      looping = true;
      return true;
    }

    function wrap() {
      if (!looping || lock) return;
      var x = track.scrollLeft;
      if (x >= origin + setWidth) { reposition(x - setWidth); }
      else if (x <= origin - setWidth) { reposition(x + setWidth); }
    }

    if (wantLoop) {
      track.addEventListener('scroll', wrap, { passive: true });
      // LAZY clone-build: cloning the slide set into 3 bands more than doubles a
      // slider's DOM, so we DON'T do it on load. We wait until the slider is
      // within ~one screen of the viewport, then build once. Sliders the visitor
      // never scrolls to never clone — initial page DOM/render stays lean.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting && buildLoop()) io.disconnect();
          });
        }, { rootMargin: '600px 0px' });
        io.observe(root);
      } else {
        if (!buildLoop()) window.addEventListener('load', buildLoop);
      }
    }

    // ---- mouse drag-to-scroll ----
    var isDown = false, startX = 0, startScroll = 0, moved = false;

    track.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      // Stop the browser's native image/link drag (ghost image) from hijacking
      // the gesture — without this, grabbing a product card image won't scroll.
      e.preventDefault();
      isDown = true;
      moved = false;
      startX = e.pageX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    window.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startScroll - dx;
    });
    window.addEventListener('mouseup', function () {
      if (!isDown) return;
      isDown = false;
      setTimeout(function () { track.classList.remove('is-dragging'); }, 0);
    });
    // suppress click navigation right after a drag
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);

    // ---- prev/next buttons + "current / total" counter (finite sliders) ----
    var prevBtn = root.querySelector('[data-home-slider-prev]');
    var nextBtn = root.querySelector('[data-home-slider-next]');
    var currentEl = root.querySelector('[data-home-slider-current]');
    var totalEl = root.querySelector('[data-home-slider-total]');

    function navSlides() {
      var marked = track.querySelectorAll('[data-home-slide]:not([data-clone])');
      return marked.length ? marked : track.children;
    }
    function navStep() {
      var s = navSlides()[0];
      if (!s) return track.clientWidth;
      var cs = getComputedStyle(track);
      var g = parseFloat(cs.columnGap || cs.gap || '0') || 0;
      return s.getBoundingClientRect().width + g;
    }
    function navUpdate() {
      if (!currentEl) return;
      var n = navSlides().length || 1;
      var idx = Math.round(track.scrollLeft / navStep()) + 1;
      currentEl.textContent = Math.max(1, Math.min(n, idx));
    }
    if (totalEl) totalEl.textContent = navSlides().length;
    if (prevBtn) prevBtn.addEventListener('click', function () { track.scrollBy({ left: -navStep(), behavior: 'smooth' }); });
    if (nextBtn) nextBtn.addEventListener('click', function () { track.scrollBy({ left: navStep(), behavior: 'smooth' }); });
    if (currentEl) { track.addEventListener('scroll', navUpdate, { passive: true }); navUpdate(); }

    // ---- autoplay ----
    if (root.hasAttribute('data-autoplay')) {
      var speed = parseInt(root.getAttribute('data-autoplay-speed'), 10) || 5000;
      var timer = null;

      var slideStep = function () {
        var slide = track.querySelector('[data-home-slide]');
        if (!slide) return track.clientWidth * 0.5;
        return slide.getBoundingClientRect().width + gap();
      };

      var advance = function () {
        var step = slideStep();
        if (looping) {
          // pre-wrap so the smooth animation never crosses the clone boundary
          if (track.scrollLeft + step >= origin + setWidth) {
            reposition(track.scrollLeft - setWidth);
          }
          track.scrollBy({ left: step, behavior: 'smooth' });
        } else {
          var maxScroll = track.scrollWidth - track.clientWidth - 2;
          if (track.scrollLeft >= maxScroll) {
            track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            track.scrollBy({ left: step, behavior: 'smooth' });
          }
        }
      };

      var start = function () { if (!timer) timer = setInterval(advance, speed); };
      var stop = function () { clearInterval(timer); timer = null; };

      root.addEventListener('mouseenter', stop);
      root.addEventListener('mouseleave', start);
      track.addEventListener('touchstart', stop, { passive: true });
      track.addEventListener('pointerdown', stop);

      // Only autoplay when in view (and respect reduced-motion).
      var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReduced) {
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (en) { en.isIntersecting ? start() : stop(); });
          }, { threshold: 0.2 }).observe(root);
        } else {
          start();
        }
      }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-home-slider]').forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // expose for dynamically-added sliders (theme editor)
  window.initHomeSliders = initAll;
})();
